import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, map, catchError, of, tap } from 'rxjs';
import { AttendanceApiService } from '../../../services/attendance-api.service';
import { ExpertApiService } from '../../../services/expert-api.service';
import {
  AttendanceDTO,
  AttendanceSessionDTO,
  RecordArrivalDTO,
  RecordDepartureDTO,
} from '../../../types/attendance.types';
import { ExpertDTO } from '../../../types/expert.types';
import { TApiErrorResponse } from '@sandbox-platform/shared-ui';

export type SignInStatusFilter = 'all' | 'signedIn' | 'signedOut';

// Grouped expert interface for alphabet grouping
export interface GroupedExpert {
  id: string;
  expert_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  industry_id: string;
  industry_name: string;
  organization_id?: string;
  organization_name?: string;
  is_active: boolean;
  current_attendance?: AttendanceDTO;
  is_signed_in: boolean;
  active_sessions: AttendanceSessionDTO[];
  total_work_duration: number; // in minutes
  session_count: number;
  first_arrival_time?: Date;
  last_departure_time?: Date;
}

export interface AttendanceTrackingState {
  // Data
  experts: GroupedExpert[];
  attendanceRecords: AttendanceDTO[];

  // UI State
  selectedLetter: string | null;
  selectedExpert: GroupedExpert | null;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | TApiErrorResponse | null;

  // Action state
  showConfirmationModal: boolean;
  pendingAction: 'signin' | 'signout' | null;

  // Filters and search
  searchTerm: string;
  signInStatusFilter: SignInStatusFilter;
}

const initialState: AttendanceTrackingState = {
  experts: [],
  attendanceRecords: [],
  selectedLetter: null,
  selectedExpert: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
  showConfirmationModal: false,
  pendingAction: null,
  searchTerm: '',
  signInStatusFilter: 'all',
};

// Helper function to process expert attendance data
const processExpertAttendance = (
  expert: ExpertDTO,
  attendanceRecord?: AttendanceDTO
): GroupedExpert => {
  const activeSessions =
    attendanceRecord?.sessions?.filter((session) => session.is_active) || [];
  const allSessions = attendanceRecord?.sessions || [];

  // Expert is signed in if they have at least one active session
  const isSignedIn = activeSessions.length > 0;

  // Calculate times based on sessions
  const firstArrivalTime =
    allSessions.length > 0
      ? new Date(
          Math.min(
            ...allSessions.map((s) => new Date(s.arrival_time).getTime())
          )
        )
      : undefined;

  const lastDepartureTime =
    allSessions.filter((s) => s.departure_time).length > 0
      ? new Date(
          Math.max(
            ...allSessions
              .filter((s) => s.departure_time)
              .map((s) => new Date(s.departure_time!).getTime())
          )
        )
      : undefined;

  return {
    id: expert.id,
    expert_id: expert.expert_id,
    first_name: expert.first_name,
    last_name: expert.last_name,
    full_name: expert.full_name,
    email: expert.email,
    industry_id: expert.industry_id,
    industry_name: expert.industry_name,
    organization_id: expert.organization_id,
    organization_name: expert.organization_name,
    is_active: expert.is_active,
    current_attendance: attendanceRecord,
    is_signed_in: isSignedIn,
    active_sessions: activeSessions,
    total_work_duration: attendanceRecord?.total_work_duration || 0,
    session_count: allSessions.length,
    first_arrival_time: firstArrivalTime,
    last_departure_time: lastDepartureTime,
  } as GroupedExpert;
};

export const AttendanceTrackingStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    // Alphabet letters with expert counts
    alphabetLetters: computed(() => {
      const experts = store.experts();
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

      return alphabet.map((letter) => {
        const count = experts.filter(
          (expert) =>
            expert.first_name.toUpperCase().startsWith(letter) &&
            expert.is_active
        ).length;

        return {
          letter,
          count,
          hasExperts: count > 0,
        };
      });
    }),

    // Filtered experts based on selected letter and search
    filteredExperts: computed(() => {
      let experts = store.experts().filter((expert) => expert.is_active);

      // Filter by selected letter
      if (store.selectedLetter()) {
        experts = experts.filter((expert) =>
          expert.first_name.toUpperCase().startsWith(store.selectedLetter()!)
        );
      }

      // Filter by search term
      if (store.searchTerm()) {
        const searchLower = store.searchTerm().toLowerCase();
        experts = experts.filter(
          (expert) =>
            expert.full_name.toLowerCase().includes(searchLower) ||
            expert.expert_id.toLowerCase().includes(searchLower) ||
            expert.email.toLowerCase().includes(searchLower) ||
            expert.industry_name.toLowerCase().includes(searchLower) ||
            (expert.organization_name &&
              expert.organization_name.toLowerCase().includes(searchLower))
        );
      }

      // Filter by sign-in status
      if (store.signInStatusFilter() === 'signedIn') {
        experts = experts.filter((expert) => expert.is_signed_in);
      } else if (store.signInStatusFilter() === 'signedOut') {
        experts = experts.filter((expert) => !expert.is_signed_in);
      }

      // Sort by name
      return experts.sort((a, b) => a.full_name.localeCompare(b.full_name));
    }),

    // Statistics
    attendanceStats: computed(() => {
      const experts = store.experts().filter((expert) => expert.is_active);
      const signedIn = experts.filter((expert) => expert.is_signed_in);
      const signedOut = experts.filter((expert) => !expert.is_signed_in);
      const totalActiveSessions = experts.reduce(
        (sum, expert) => sum + expert.active_sessions.length,
        0
      );
      const totalSessions = experts.reduce(
        (sum, expert) => sum + expert.session_count,
        0
      );

      return {
        total: experts.length,
        signedIn: signedIn.length,
        signedOut: signedOut.length,
        signedInPercentage:
          experts.length > 0
            ? Math.round((signedIn.length / experts.length) * 100)
            : 0,
        totalActiveSessions,
        totalSessions,
        averageSessionsPerExpert:
          experts.length > 0
            ? Math.round((totalSessions / experts.length) * 100) / 100
            : 0,
      };
    }),

    // Selected letter experts count
    selectedLetterCount: computed(() => {
      if (!store.selectedLetter()) return 0;
      return store
        .experts()
        .filter(
          (expert) =>
            expert.first_name
              .toUpperCase()
              .startsWith(store.selectedLetter()!) && expert.is_active
        ).length;
    }),

    // Check if expert can sign in/out
    canPerformAction: computed(() => {
      return !store.isSubmitting() && !store.isLoading();
    }),
  })),
  withMethods(
    (
      store,
      attendanceApi = inject(AttendanceApiService),
      expertApi = inject(ExpertApiService)
    ) => ({
      // Load experts and their current attendance status
      loadExperts: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true, error: null })),
          switchMap(() =>
            expertApi.get({ page: 1, limit: 1000 }).pipe(
              switchMap((expertResponse) => {
                if (expertResponse.status != 200 || !expertResponse.data) {
                  throw new Error('Failed to load experts');
                }

                const experts = expertResponse.data.experts;
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Get today's attendance for all experts
                return attendanceApi
                  .get({
                    date_from: today,
                    date_to: new Date(today.getTime() + 24 * 60 * 60 * 1000),
                    page: 1,
                    limit: 1000,
                  })
                  .pipe(
                    map((attendanceResponse) => {
                      const attendanceRecords =
                        attendanceResponse.status == 200 &&
                        attendanceResponse.data
                          ? attendanceResponse.data.attendance
                          : [];

                      // Map experts with their attendance status using session-based logic
                      const groupedExperts: GroupedExpert[] = experts.map(
                        (expert) => {
                          const todayAttendance = attendanceRecords.find(
                            (record) =>
                              record.expert_id === expert.id &&
                              new Date(record.date).toDateString() ===
                                today.toDateString()
                          );

                          return processExpertAttendance(
                            expert,
                            todayAttendance
                          );
                        }
                      );

                      return { experts: groupedExperts, attendanceRecords };
                    })
                  );
              }),
              tap({
                next: ({
                  experts,
                  attendanceRecords,
                }: {
                  experts: GroupedExpert[];
                  attendanceRecords: AttendanceDTO[];
                }) => {
                  patchState(store, {
                    experts,
                    attendanceRecords,
                    isLoading: false,
                    error: null,
                  });
                },
                error: (error: any) => {
                  patchState(store, {
                    isLoading: false,
                    error:
                      error.message ||
                      'Failed to load experts and attendance data',
                  });
                },
              })
            )
          )
        )
      ),

      // Select a letter from alphabet
      selectLetter: (letter: string | null) => {
        patchState(store, { selectedLetter: letter, selectedExpert: null });
      },

      // Select an expert
      selectExpert: (expert: GroupedExpert | null) => {
        patchState(store, { selectedExpert: expert });
      },

      // Show confirmation modal for action
      showActionConfirmation: (
        action: 'signin' | 'signout',
        expert: GroupedExpert
      ) => {
        patchState(store, {
          selectedExpert: expert,
          pendingAction: action,
          showConfirmationModal: true,
        });
      },

      // Hide confirmation modal
      hideActionConfirmation: () => {
        patchState(store, {
          showConfirmationModal: false,
          pendingAction: null,
        });
      },

      // Set pending action
      setPendingAction: (action: 'signin' | 'signout' | null) => {
        patchState(store, {
          pendingAction: action,
        });
      },

      // Clear pending action
      clearPendingAction: () => {
        patchState(store, {
          pendingAction: null,
        });
      },

      // Record attendance (sign in) - creates a new session
      recordSignIn: rxMethod<{
        expertId: string;
        note?: string;
        location?: any;
      }>(
        pipe(
          tap(() => patchState(store, { isSubmitting: true, error: null })),
          switchMap(({ expertId, note, location }) => {
            const recordData: RecordArrivalDTO = {
              expert_id: expertId,
              arrival_time: new Date(),
              note: note,
              location: location || {
                type: 'office',
              },
              force_new_session: false, // Allow system to decide based on business logic
            };

            return attendanceApi.recordArrival(recordData).pipe(
              tap({
                next: (response: any) => {
                  if (response.status == 201 || response.status == 200) {
                    // Update the expert's attendance status with new session data
                    const updatedExperts = store.experts().map((expert) => {
                      if (expert.id === expertId) {
                        const updatedAttendance =
                          response.data as AttendanceDTO;
                        return processExpertAttendance(
                          {
                            id: expert.id,
                            expert_id: expert.expert_id,
                            first_name: expert.first_name,
                            last_name: expert.last_name,
                            full_name: expert.full_name,
                            email: expert.email,
                            industry_id: expert.industry_id,
                            industry_name: expert.industry_name,
                            organization_id: expert.organization_id,
                            organization_name: expert.organization_name,
                            is_active: expert.is_active,
                          } as ExpertDTO,
                          updatedAttendance
                        );
                      }
                      return expert;
                    });

                    patchState(store, {
                      experts: updatedExperts,
                      isSubmitting: false,
                      showConfirmationModal: false,
                      pendingAction: null,
                      error: null,
                    });
                  } else {
                    throw new Error(
                      response.message || 'Failed to record sign in'
                    );
                  }
                },
                error: (error: any) => {
                  patchState(store, {
                    isSubmitting: false,
                    error: error.message || 'Failed to record sign in',
                  });
                },
              })
            );
          })
        )
      ),

      // Record departure (sign out) - closes active session(s)
      recordSignOut: rxMethod<{
        expertId: string;
        note?: string;
        sessionId?: string;
      }>(
        pipe(
          tap(() => patchState(store, { isSubmitting: true, error: null })),
          switchMap(({ expertId, note, sessionId }) => {
            const recordData: RecordDepartureDTO = {
              expert_id: expertId,
              departure_time: new Date(),
              note: note,
              session_id: sessionId, // If provided, close specific session; otherwise, close latest active session
            };

            return attendanceApi.recordDeparture(recordData).pipe(
              tap({
                next: (response: any) => {
                  if (response.status == 201 || response.status == 200) {
                    // Update the expert's attendance status with updated session data
                    const updatedExperts = store.experts().map((expert) => {
                      if (expert.id === expertId) {
                        const updatedAttendance =
                          response.data as AttendanceDTO;
                        return processExpertAttendance(
                          {
                            id: expert.id,
                            expert_id: expert.expert_id,
                            first_name: expert.first_name,
                            last_name: expert.last_name,
                            full_name: expert.full_name,
                            email: expert.email,
                            industry_id: expert.industry_id,
                            industry_name: expert.industry_name,
                            organization_id: expert.organization_id,
                            organization_name: expert.organization_name,
                            is_active: expert.is_active,
                          } as ExpertDTO,
                          updatedAttendance
                        );
                      }
                      return expert;
                    });

                    patchState(store, {
                      experts: updatedExperts,
                      isSubmitting: false,
                      showConfirmationModal: false,
                      pendingAction: null,
                      error: null,
                    });
                  } else {
                    throw new Error(
                      response.message || 'Failed to record sign out'
                    );
                  }
                },
                error: (error: any) => {
                  patchState(store, {
                    isSubmitting: false,
                    error: error.message || 'Failed to record sign out',
                  });
                },
              })
            );
          })
        )
      ),

      // Update search term
      updateSearchTerm: (searchTerm: string) => {
        patchState(store, { searchTerm });
      },

      // update sign in status filter
      updateSignInStatusFilter: (status: SignInStatusFilter) => {
        patchState(store, { signInStatusFilter: status });
      },

      // Clear error
      clearError: () => {
        patchState(store, { error: null });
      },

      // Refresh data
      refresh: rxMethod<void>(
        pipe(
          tap(() => {
            // Clear current state and reload
            patchState(store, {
              isLoading: true,
              error: null,
              selectedLetter: null,
              selectedExpert: null,
              searchTerm: '',
              signInStatusFilter: 'all',
            });
          }),
          switchMap(() =>
            expertApi.get({ page: 1, limit: 1000 }).pipe(
              switchMap((expertResponse) => {
                if (expertResponse.status != 200 || !expertResponse.data) {
                  throw new Error('Failed to load experts');
                }

                const experts = expertResponse.data.experts;
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                // Get today's attendance for all experts
                return attendanceApi
                  .get({
                    date_from: today,
                    date_to: new Date(today.getTime() + 24 * 60 * 60 * 1000),
                    page: 1,
                    limit: 1000,
                  })
                  .pipe(
                    map((attendanceResponse) => {
                      const attendanceRecords =
                        attendanceResponse.status == 200 &&
                        attendanceResponse.data
                          ? attendanceResponse.data.attendance
                          : [];

                      // Map experts with their attendance status using session-based logic
                      const groupedExperts: GroupedExpert[] = experts.map(
                        (expert) => {
                          const todayAttendance = attendanceRecords.find(
                            (record) =>
                              record.expert_id === expert.id &&
                              new Date(record.date).toDateString() ===
                                today.toDateString()
                          );

                          return processExpertAttendance(
                            expert,
                            todayAttendance
                          );
                        }
                      );

                      return { experts: groupedExperts, attendanceRecords };
                    })
                  );
              }),
              tap({
                next: ({
                  experts,
                  attendanceRecords,
                }: {
                  experts: GroupedExpert[];
                  attendanceRecords: AttendanceDTO[];
                }) => {
                  patchState(store, {
                    experts,
                    attendanceRecords,
                    isLoading: false,
                    error: null,
                  });
                },
                error: (error: any) => {
                  patchState(store, {
                    isLoading: false,
                    error:
                      error.message ||
                      'Failed to load experts and attendance data',
                  });
                },
              })
            )
          )
        )
      ),
    })
  )
);
